import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import "./css/style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faCertificate,
  faRocket,
  faBookOpen,
  faArrowRight,
  faCheck,
  faUsers,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";

function Goals() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-stagger").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Navbar page={"goals"} />

      <div className="page-banner">
        <h1>Your Learning Goals</h1>
        <p>Whether you want to upskill, earn a degree, or advance your career — we have a pathway for you.</p>
      </div>

      <section className="goals-section">
        {/* Take a Free Course */}
        <div className="goal-row reveal">
          <div className="goal-image">
            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80"
              alt="Students learning online"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop&q=80";
              }}
            />
          </div>
          <div className="goal-content">
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#eff6ff", color: "#2563eb", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700, marginBottom: "16px" }}>
              <FontAwesomeIcon icon={faBookOpen} /> Free Access
            </div>
            <h3>Take a Free Course</h3>
            <p>
              Explore our selection of complimentary courses designed to give you a taste of what Consortium eLearning offers. From introductory modules in computer science to foundational business courses, you can start learning without any financial commitment.
            </p>
            <p>
              Our free courses include video lectures, interactive quizzes, and community discussion access — everything you need to begin your learning journey.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: "16px 0" }}>
              {["No credit card required", "Full access to course materials", "Community forum participation", "Certificate of completion"].map((item, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", color: "#64748b", fontSize: "0.92rem" }}>
                  <FontAwesomeIcon icon={faCheck} style={{ color: "#10b981", fontSize: "0.8rem" }} /> {item}
                </li>
              ))}
            </ul>
            <Link to="/courses" className="btn-primary" style={{ padding: "12px 28px", fontSize: "0.92rem" }}>
              Browse Courses <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>

        {/* Earn a Degree */}
        <div className="goal-row reverse reveal">
          <div className="goal-image">
            <img
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&auto=format&fit=crop&q=80"
              alt="University graduation and degree"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&auto=format&fit=crop&q=80";
              }}
            />
          </div>
          <div className="goal-content">
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#fef3c7", color: "#d97706", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700, marginBottom: "16px" }}>
              <FontAwesomeIcon icon={faGraduationCap} /> Degree Programs
            </div>
            <h3>Earn a Degree</h3>
            <p>
              Partner with leading universities and academic institutions worldwide to earn accredited degrees entirely online. Our degree pathways cover undergraduate, postgraduate, and professional programs across diverse disciplines.
            </p>
            <p>
              Study at your own pace with structured curricula, mentorship from academic experts, and access to a global learning community of ambitious peers.
            </p>
            <Link to="/accreditations" className="btn-outline" style={{ padding: "12px 28px", fontSize: "0.92rem" }}>
              View Accredited Programs <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>

        {/* Earn a Certificate */}
        <div className="goal-row reveal">
          <div className="goal-image">
            <img
              src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop&q=80"
              alt="Professional certification"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop&q=80";
              }}
            />
          </div>
          <div className="goal-content">
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#d1fae5", color: "#059669", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700, marginBottom: "16px" }}>
              <FontAwesomeIcon icon={faCertificate} /> Certifications
            </div>
            <h3>Earn a Certificate</h3>
            <p>
              Complete specialized courses and earn professional certificates recognized by employers and institutions globally. Our certifications validate your expertise in specific domains, from data science to project management.
            </p>
            <p>
              Each certificate includes a unique verification code, shareable badge, and downloadable PDF — perfect for enhancing your professional profile and standing out in the job market.
            </p>
            <Link to="/courses" className="btn-primary" style={{ padding: "12px 28px", fontSize: "0.92rem" }}>
              Explore Certificate Courses <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>

        {/* Career Development */}
        <div className="goal-row reverse reveal">
          <div className="goal-image">
            <img
              src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&auto=format&fit=crop&q=80"
              alt="Career growth"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&auto=format&fit=crop&q=80";
              }}
            />
          </div>
          <div className="goal-content">
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#e0f2fe", color: "#0284c7", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700, marginBottom: "16px" }}>
              <FontAwesomeIcon icon={faRocket} /> Career Growth
            </div>
            <h3>Career Development</h3>
            <p>
              Accelerate your professional growth with our comprehensive career development resources. Our platform offers skills gap analysis, personalized learning paths, mentorship programs, and career coaching from industry veterans.
            </p>
            <p>
              Whether you are transitioning industries, seeking a promotion, or building entirely new competencies, our career development track is designed to help you achieve measurable professional outcomes.
            </p>
            <Link to="/get-started" className="btn-outline" style={{ padding: "12px 28px", fontSize: "0.92rem" }}>
              Start Your Journey <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        padding: "56px 8%", background: "var(--gradient-hero)",
        display: "flex", justifyContent: "center", gap: "60px", flexWrap: "wrap", textAlign: "center"
      }}>
        {[
          { icon: faCertificate, label: "Certificates Issued", value: "50K+" },
          { icon: faUsers, label: "Active Learners", value: "10K+" },
          { icon: faGlobe, label: "Countries", value: "180+" },
          { icon: faGraduationCap, label: "Expert Instructors", value: "200+" }
        ].map((s, i) => (
          <div key={i} style={{ color: "#fff" }}>
            <FontAwesomeIcon icon={s.icon} style={{ color: "#38bdf8", fontSize: "1.5rem", marginBottom: "8px", display: "block" }} />
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "2rem", fontWeight: 800 }}>{s.value}</div>
            <div style={{ color: "#94a3b8", fontSize: "0.88rem", fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
}

export default Goals;
